<template>
  <div class="filter-container">
    <label class="filter-label" for="search-filter-select">{{ TEXT.searchIn }}</label>
    <v-select
      id="search-filter-select"
      v-model="selectedOption"
      :items="filterOptions"
      item-text="label"
      item-value="id"
      return-object
      outlined
      dense
      hide-details
      class="filter-select"
      :menu-props="{ bottom: true, offsetY: true, top: false }"
      @change="onSelectChange"
      @focus="isMenuOpen = true"
      @blur="isMenuOpen = false"
    >
      <template v-slot:selection="{ item }">
        <div class="select-content">
          <img :src="item.icon" class="select-icon" />
          <span class="select-text">{{ item.selectedText || item.label }}</span>
        </div>
      </template>
      <template v-slot:append>
        <div class="chevron-container">
          <img :src="ICONS.chevronDown" :class="['chevron-icon', { 'chevron-rotated': isMenuOpen }]" />
        </div>
      </template>
      <template v-slot:item="{ item }">
        <div class="menu-item-content">
          <img :src="item.icon" class="menu-item-icon" />
          <div class="menu-item-text-wrapper">
            <span class="menu-item-label">{{ item.label }}</span>
            <span v-if="item.description" class="menu-item-description">{{ item.description }}</span>
          </div>
        </div>
      </template>
    </v-select>

    <!-- Prompt Dialog para selección de elemento específico -->
    <PromptDialogComponent
      v-if="openedPrompt"
      :title="promptTitle"
      :isOpened="openedPrompt"
      :context="promptContext"
      :formSchema="currentFormSchema"
      :formFieldValues="formData"
      :width="640"
      @execute-action="handleExecutePromptAction"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n-composable'
import { ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import PromptDialogComponent from '@/components/Dialog/PromptDialogComponent.vue'
import { useSearchIAStore } from '@/modules/documentSearcher/stores/useSearchIAStore'
import type { FilterOption, FilterType } from '@/modules/documentSearcher/types/SearchIATypes'
import { FilterTypes } from '@/modules/documentSearcher/types/SearchIATypes'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'

// PROPS
const props = defineProps({
  defaultFilter: {
    type: String,
    default: ''
  }
})

// EMIT
const emit = defineEmits(['filter-selected'])

// STORE
const searchIAStore = useSearchIAStore()

// TRANSLATIONS
const { t } = useI18n()
const TEXT = {
  documentsInContactExpedients: t('components.chat_filter.documents_in_contact_expedients') as string,
  general: t('views.search_ia.general') as string,
  generalDescription: t('views.search_ia.general_description') as string,
  labelExpedient: t('components.chat_filter.expedients') as string,
  searchIn: t('views.search_ia.search_in') as string,
  selectContact: t('views.search_ia.select_contact') as string,
  selectContactTitle: t('components.chat_filter.select_contact') as string,
  selectExpedient: t('views.search_ia.select_expedient') as string,
  selectExpedientTitle: t('components.chat_filter.select_expedient') as string,
  selectPlaceholder: t('components.chat_filter.select_placeholder') as string
}

// ICONS
const ICONS = {
  [FilterTypes.GENERAL]: require('@/assets/images/svg/icon-documents.svg'),
  [FilterTypes.EXPEDIENT]: require('@/assets/images/svg/icon-law.svg'),
  [FilterTypes.CONTACT]: require('@/assets/images/svg/icon-agenda.svg'),
  chevronDown: require('@/assets/images/svg/icon-chevron-down.svg')
}

// DATA
const selectedOption = ref<FilterOption | null>(null)
const selectedFilter = ref(props.defaultFilter)
const promptTitle = ref('')
const promptContext = ref('filterSelectionForm')
const openedPrompt = ref(false)
const currentFormSchema = ref<any[]>([])
const formData = ref<any>({})
const selectedEntityName = ref<string>('')
const isMenuOpen = ref(false)

// Helper to create filter option
const createFilterOption = (
  id: FilterType,
  label: string,
  description: string | null,
  selectedText?: string
): FilterOption => ({
  id,
  label,
  description,
  icon: ICONS[id],
  selectedText
})

// Filter options for the select
const filterOptions = computed((): FilterOption[] => [
  createFilterOption(FilterTypes.GENERAL, TEXT.general, TEXT.generalDescription),
  createFilterOption(
    FilterTypes.EXPEDIENT,
    TEXT.selectExpedient,
    null,
    selectedFilter.value === FilterTypes.EXPEDIENT ? selectedEntityName.value : undefined
  ),
  createFilterOption(
    FilterTypes.CONTACT,
    TEXT.selectContact,
    null,
    selectedFilter.value === FilterTypes.CONTACT ? selectedEntityName.value : undefined
  )
])

// Set initial selection to 'general'
selectedOption.value = filterOptions.value[0]

// Form Schemas
const parsedFormSchemaExpedients = [
  {
    id: 'expedientId',
    lg: 12,
    md: 12,
    xl: 12,
    name: 'expedientId',
    items: 'types',
    keyId: 'ref',
    label: TEXT.labelExpedient,
    keyName: 'name',
    keyValue: 'ref',
    fieldType: 'LexonSelectComponent',
    placeholder: TEXT.selectPlaceholder,
    tableHeaders: [
      {
        table: 'default',
        columns: [
          {
            name: 'Ref',
            width: 20
          },
          {
            name: 'Description',
            width: 40
          },
          {
            name: 'customerName',
            width: 40
          }
        ]
      }
    ],
    endPointEntity: {
      url: 'action/contact/EXP',
      method: 'POST',
      params: [
        {
          name: 'order',
          value: 'name'
        },
        {
          name: 'typeOrder',
          value: 'asc'
        }
      ]
    },
    validationRules: 'required|objectNotEmpty',
    serverPagination: true,
    returnObject: false,
    attach: false
  }
]

const parsedFormSchemaContacts = [
  {
    id: 'name',
    name: 'name',
    type: 'text',
    label: TEXT.documentsInContactExpedients,
    fieldType: 'LexonSelectComponent',
    loadMethod: 'contacts/all/get',
    validationRules: 'required',
    itemName: 'name',
    itemValue: 'name',
    tableHeaders: ['Name', 'Email', 'MobilePhone', 'Phone'],
    attach: false,
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12
  }
]

// COMPUTED AND METHODS
const resetToGeneral = () => {
  selectedFilter.value = ''
  selectedEntityName.value = ''
  searchIAStore.resetFilter()
  selectedOption.value = filterOptions.value[0]
}

const onSelectChange = (item: FilterOption) => {
  trackEvent(TrackerEvents.IA_FILTER_APPLIED, { filter_type: item.id })
  if (item.id === FilterTypes.GENERAL) {
    resetToGeneral()
  } else {
    openFilterPrompt(item)
  }
}

const FILTER_CONFIG = {
  [FilterTypes.EXPEDIENT]: {
    title: TEXT.selectExpedientTitle,
    schema: parsedFormSchemaExpedients,
    formField: 'expedientId'
  },
  [FilterTypes.CONTACT]: {
    title: TEXT.selectContactTitle,
    schema: parsedFormSchemaContacts,
    formField: 'name'
  }
} as const

const openFilterPrompt = (item: FilterOption) => {
  selectedFilter.value = item.id
  formData.value = {}

  if (item.id === FilterTypes.EXPEDIENT || item.id === FilterTypes.CONTACT) {
    const config = FILTER_CONFIG[item.id]
    promptTitle.value = config.title
    currentFormSchema.value = config.schema
    openedPrompt.value = true
  }
}

const extractEntityName = (data: any): string => {
  if (typeof data === 'object') {
    // Si existe name, construir nombre completo con apellidos
    if (data.name) {
      const fullName = [data.name, data.firstSurname, data.secondSurname].filter(Boolean).join(' ')
      return fullName || data.name
    }
    return data.Description || data.description || data.Name || data.Ref || data.ref || ''
  }
  return String(data)
}

const handleFilterSelection = (filterType: FilterType, formFieldKey: string) => {
  const fieldData = formData.value[formFieldKey]
  if (!fieldData) return

  const entityName = extractEntityName(fieldData)
  selectedEntityName.value = entityName
  selectedOption.value = createFilterOption(filterType, entityName, null, entityName)

  const filterValue = typeof fieldData === 'object' ? fieldData.id || fieldData : fieldData
  searchIAStore.setFilter(filterType, filterValue, entityName)
}

const handleExecutePromptAction = (actionName: ActionName) => {
  if (actionName === ActionName.CLOSE) {
    openedPrompt.value = false
    resetToGeneral()
    return
  }

  if (actionName === ActionName.SAVE) {
    if (selectedFilter.value === FilterTypes.EXPEDIENT) {
      handleFilterSelection(FilterTypes.EXPEDIENT, 'expedientId')
    } else if (selectedFilter.value === FilterTypes.CONTACT) {
      handleFilterSelection(FilterTypes.CONTACT, 'name')
    }
    openedPrompt.value = false
  }
}

// Initialize from store on mount
onMounted(() => {
  if (searchIAStore.filterType && searchIAStore.filterEntityName) {
    selectedFilter.value = searchIAStore.filterType
    selectedEntityName.value = searchIAStore.filterEntityName

    // Find the option and set it
    const option = filterOptions.value.find((opt) => opt.id === searchIAStore.filterType)
    if (option) {
      selectedOption.value = {
        ...option,
        label: searchIAStore.filterEntityName,
        selectedText: searchIAStore.filterEntityName
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.filter-container {
  display: flex;
  align-items: center;
  gap: $spacing-xs;
}

.filter-label {
  font-family: $inter-font;
  color: $neutral-grey-500;
  white-space: nowrap;
  font-size: $fontSize-xxs;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-left: $spacing-xxs;
}

.filter-select {
  max-width: 185px;

  ::v-deep .v-input__control {
    .v-input__slot {
      min-height: 36px;
      padding: 0 8px;
    }

    .v-select__selection {
      margin: 0;
    }
  }

  .select-content {
    display: flex;
    align-items: center;
    gap: $spacing-xs;
    width: 100%;
  }

  .select-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .select-text {
    font-family: $inter-font;
    color: $neutral-grey-900;
    font-size: $fontSize-xs;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chevron-container {
    display: flex;
    align-items: center;
    border-left: 1px solid $neutral-grey-300;
    padding-left: $spacing-xs;
    margin-left: $spacing-xs;
    height: 100%;
  }

  .chevron-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    filter: invert(9%) sepia(99%) saturate(6453%) hue-rotate(233deg) brightness(88%) contrast(148%);
    transition: transform 0.3s ease;

    &.chevron-rotated {
      transform: rotate(180deg);
    }
  }
}

.menu-item-content {
  display: flex;
  align-items: center;
  gap: $spacing-xs;
  width: 100%;

  .menu-item-icon {
    width: 16px !important;
    height: 16px !important;
    max-width: 16px !important;
    max-height: 16px !important;
    flex-shrink: 0;
    object-fit: contain;
  }

  .menu-item-text-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0px;
    flex: 1;
  }

  .menu-item-label {
    font-family: $inter-font;
    font-size: $fontSize-xs;
    font-weight: 500;
    color: $neutral-grey-900;
  }

  .menu-item-description {
    font-family: $inter-font;
    font-size: $fontSize-xxs;
    color: $neutral-grey-600;
  }
}

::v-deep {
  .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center !important;

    .v-icon {
      font-size: $fontSize-lg !important;
    }
  }
}
</style>
